import AppPage from "layouts/AppPage";
import { Box, HStack, VStack, Spinner, Center, useToast } from "@chakra-ui/react";
import { LuLayoutGrid, LuList } from "react-icons/lu";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { useNavbar } from "hooks/useNavbar";
import useCaminoStore from "hooks/useCaminoStore";
import { NewHireJourneyId } from "models/newHire";
import { Filter } from "models/filter";
import { createQueryString } from "helpers/queryString";
import { Heading18 } from "components/Heading";
import { SegmentedControl } from "design-system/components/SegmentedControl";
import { Task } from "features/Resources/Task/models";
import { OPEN_TASK_STATUSES, CLOSED_TASK_STATUSES, TaskStatus } from "features/Resources/Task/constants";
import useSideDrawer from "hooks/useSideDrawer";
import ActionSideDrawer from "components/ActionSideDrawer";
import { pullUniqueNewHireJourneyIdsFromTasks } from "features/Resources/Task/helpers";
import { useTaskListQuery } from "features/Resources/Task/hooks";
import { TaskCard, TaskListHeader, TasksByJourneyView, TaskSummaryBar } from "features/Resources/Task/Components/UserTaskSummary";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";
import { RandomEmptyTaskState } from "components/EmptyComponents/RandomEmptyState";
import useModal from "hooks/useModal";
import CreateTaskForm from "features/Resources/Task/Components/Form/CreateTaskForm";
import { useCreateTask } from "features/Resources/Task/hooks";
import FeatureGate from "components/FeatureGate";
import CDS from "design-system";
import MyTasksFilters, {
  MyTasksFiltersState,
  MyTasksStatusFilter,
  DEFAULT_STATUS_FILTER,
  ViewMode,
  DEFAULT_JOURNEY_SORT,
  DEFAULT_LIST_SORT,
} from "./MyTasksFilters";

const STATUS_FILTER_PARAM = "status";

function statusesForFilter(filter: MyTasksStatusFilter): Task["status"][] {
  if (filter === "not_started") return [TaskStatus.UPCOMING];
  if (filter === "closed") return CLOSED_TASK_STATUSES;
  return OPEN_TASK_STATUSES;
}

function readStatusFilter(value: string | null): MyTasksStatusFilter {
  if (value === "not_started" || value === "open" || value === "closed") return value;
  return DEFAULT_STATUS_FILTER;
}

const VIEW_MODE_OPTIONS = [
  { value: "journey" as const, label: "By New Hire", icon: LuLayoutGrid },
  { value: "list" as const, label: "List", icon: LuList },
];

const defaultFilters: MyTasksFiltersState = {};

// Pull a generous cap so journey grouping, the summary bar, and search all
// operate on a complete view of the user's work rather than a server page.
// Search runs client-side against this set.
const TASK_PAGE_SIZE = 1000;

function sortTasks(tasks: Task[], sortValue: string): Task[] {
  const sorted = [...tasks];

  switch (sortValue) {
    case "due_date_asc":
      return sorted.sort((a, b) => {
        if (!a.dueAt) return 1;
        if (!b.dueAt) return -1;
        return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
      });
    case "due_date_desc":
      return sorted.sort((a, b) => {
        if (!a.dueAt) return 1;
        if (!b.dueAt) return -1;
        return new Date(b.dueAt).getTime() - new Date(a.dueAt).getTime();
      });
    case "start_date_asc":
      return sorted.sort((a, b) => {
        if (!a.effectiveStartDate) return 1;
        if (!b.effectiveStartDate) return -1;
        return new Date(a.effectiveStartDate).getTime() - new Date(b.effectiveStartDate).getTime();
      });
    case "start_date_desc":
      return sorted.sort((a, b) => {
        if (!a.effectiveStartDate) return 1;
        if (!b.effectiveStartDate) return -1;
        return new Date(b.effectiveStartDate).getTime() - new Date(a.effectiveStartDate).getTime();
      });
    case "name_asc":
      return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "name_desc":
      return sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    default:
      return sorted;
  }
}

export default function MyTasksPage() {
  const user = useCaminoStore((state) => state.currentUser);
  const credentialType = useCaminoStore((state) => state.credentialType);
  const [filters, setFilters] = useState<MyTasksFiltersState>(defaultFilters);
  const [viewMode, setViewMode] = useState<ViewMode>("journey");
  const [sort, setSort] = useState<string>(DEFAULT_JOURNEY_SORT);
  const { setPageTitle } = useNavbar();
  const [initialJourneyIds, setInitialJourneyIds] = useState<NewHireJourneyId[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  // Sync newHireId + status filter from URL search params into filters
  const newHireIdFromUrl = searchParams.get("newHireId") || null;
  const statusFilterFromUrl = readStatusFilter(searchParams.get(STATUS_FILTER_PARAM));
  useEffect(() => {
    if (newHireIdFromUrl && newHireIdFromUrl !== filters.newHireId) {
      setFilters((prev) => ({ ...prev, newHireId: newHireIdFromUrl })); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [newHireIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (statusFilterFromUrl !== (filters.statusFilter ?? DEFAULT_STATUS_FILTER)) {
      setFilters((prev) => ({ ...prev, statusFilter: statusFilterFromUrl })); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [statusFilterFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    Modal: CreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal({
    headerTitle: "Create New Task",
    size: "xl",
  });

  const { mutate: createTask, isPending: isCreating } = useCreateTask();

  const handleFiltersChange = useCallback(
    (newFilters: MyTasksFiltersState) => {
      setFilters(newFilters);

      if (newFilters.newHireId) {
        searchParams.set("newHireId", newFilters.newHireId);
      } else {
        searchParams.delete("newHireId");
      }
      const status = newFilters.statusFilter ?? DEFAULT_STATUS_FILTER;
      if (status === DEFAULT_STATUS_FILTER) {
        searchParams.delete(STATUS_FILTER_PARAM);
      } else {
        searchParams.set(STATUS_FILTER_PARAM, status);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    searchParams.delete("newHireId");
    searchParams.delete(STATUS_FILTER_PARAM);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    // Reset sort to the default for the new view mode
    setSort(mode === "journey" ? DEFAULT_JOURNEY_SORT : DEFAULT_LIST_SORT);
  }, []);

  useEffect(() => {
    setPageTitle("My Tasks");
    return () => {
      setPageTitle(null);
    };
  }, [setPageTitle]);

  // Build query params for task list. Search is applied client-side below so
  // typing is instant and works the same in journey grouping and list view.
  const queries = useMemo(() => {
    const statusFilter = filters.statusFilter ?? DEFAULT_STATUS_FILTER;
    return { status: statusesForFilter(statusFilter) };
  }, [filters.statusFilter]);

  // Build filters
  const queryFilters = useMemo(() => {
    const filterList: Filter[] = [];
    if (filters.newHireId) {
      filterList.push({
        fieldName: "new_hire_journey_id",
        value: filters.newHireId,
        operator: "=",
      });
    }
    if (user?.id) {
      filterList.push({
        fieldName: "assignee_id",
        value: user.id,
        operator: "=",
      });
    }
    return filterList;
  }, [filters.newHireId, user]);  

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: TASK_PAGE_SIZE },
        additionalQueries: queries,
        filters: queryFilters,
      }),
    [queries, queryFilters],
  );

  const { data: tasks = [], isLoading } = useTaskListQuery(queryString);

  // Client-side search against the loaded set — matches task name.
  // The "Filter by New Hire" dropdown covers searching by person.
  const searchedTasks = useMemo(() => {
    const needle = filters.q?.trim().toLowerCase();
    if (!needle) return tasks;
    return tasks.filter((t) => t.name?.toLowerCase().includes(needle));
  }, [tasks, filters.q]);

  const hasActiveNarrowing = Boolean(filters.q?.trim() || filters.newHireId);

  // Apply client-side sorting for list view
  const sortedTasks = useMemo(() => {
    if (viewMode === "list") {
      return sortTasks(searchedTasks, sort);
    }
    return searchedTasks;
  }, [searchedTasks, sort, viewMode]);

  // Set initial journey IDs for the select dropdown
  useEffect(() => {
    if (initialJourneyIds.length === 0 && tasks && tasks.length !== 0) {
      setInitialJourneyIds(pullUniqueNewHireJourneyIdsFromTasks(tasks)); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [initialJourneyIds.length, tasks]);

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<Task>({
    getItemId: (item) => item?.id,
  });

  const handleCreateTask = useCallback(
    (taskData: Partial<Task>) => {
      createTask(taskData, {
        onSuccess: () => {
          toast({
            title: "Task created successfully",
            status: "success",
            duration: 3000,
          });
          closeCreateModal();
        },
      });
    },
    [createTask, toast, closeCreateModal],
  );

  // Pre-start new hires sign in with personal email and belong on the
  // new-hire portal tasks view, not the work-user My Tasks page.
  if (credentialType === "personal_email") {
    return <Navigate to={SHARED_ROUTES.newHireTasks} replace />;
  }

  return (
    <AppPage>
      <VStack spacing={4} align="stretch" w="100%">
        <VStack spacing={1} align="start">
          <Heading18>My Tasks</Heading18>
          <TaskSummaryBar tasks={searchedTasks} />
        </VStack>
        <HStack justify="space-between" align="center" w="100%">
          <Box flex={1}>
            <MyTasksFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onReset={handleReset}
              viewMode={viewMode}
              initialNewHireJourneyIds={initialJourneyIds}
              currentSort={sort}
              onSortChange={setSort}
            />
          </Box>
          <HStack spacing={3} flexShrink={0}>
            <SegmentedControl options={VIEW_MODE_OPTIONS} value={viewMode} onChange={handleViewModeChange} />
            <FeatureGate feature="custom_tasks">
              <CDS.AddButton aria-label="Add task" onClick={openCreateModal} size={CDS.AddButton.size.medium} />
            </FeatureGate>
          </HStack>
        </HStack>

        {isLoading && (
          <Center py={8}>
            <Spinner />
          </Center>
        )}
        {!isLoading && tasks.length >= TASK_PAGE_SIZE && (
          <CDS.Callout variant="warning" title={`Showing the first ${TASK_PAGE_SIZE} tasks`}>
            Narrow with filters above to find the rest.
          </CDS.Callout>
        )}
        {!isLoading && searchedTasks.length === 0 && hasActiveNarrowing && (
          <Center py={8}>
            <CDS.Text color={CDS.Color.text.secondary}>No tasks match your search or filters.</CDS.Text>
          </Center>
        )}
        {!isLoading && tasks.length === 0 && !hasActiveNarrowing && <RandomEmptyTaskState />}
        {!isLoading && searchedTasks.length > 0 && viewMode === "journey" && (
          <Box width="100%">
            <TasksByJourneyView
              tasks={searchedTasks}
              onTaskClick={(t) => expandClickHandlerByItem(t)}
              sortBy={sort}
              currentUserId={user?.id}
            />
          </Box>
        )}
        {!isLoading && sortedTasks.length > 0 && viewMode === "list" && (
          <Box w="100%">
            <TaskListHeader showJourney showAssignee={false} />
            {sortedTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                compact
                onClick={(t) => expandClickHandlerByItem(t)}
                showAssignee={false}
                showJourney
              />
            ))}
          </Box>
        )}
      </VStack>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />
      <TaskActionModalByQueryParam />
      <CreateModal>
        <CreateTaskForm onSubmit={handleCreateTask} onCancel={closeCreateModal} submitIsLoading={isCreating} />
      </CreateModal>
    </AppPage>
  );
}
